import { Fragment, useEffect, useState } from 'react';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FilterMatchMode } from 'primereact/api';
import { Paginator } from 'primereact/paginator';

import { InvoiceService } from '../InvoiceService';
import { FormulaTypeService } from '../../he-thong/cau-hinh-hoa-don/FormulaTypeService';
import { ProductService } from '../../danh-muc/hang-hoa-dich-vu/ProductService';
import { UnitService } from '../../danh-muc/don-vi-tinh/UnitService';

import { INVOICE_DETAIL_REMOVED_FIELDS, InvoiceField } from './Initial';
import { manufacturingCountryOptions } from './CountryCatalogOptions';

import { useCellEditing } from '../../../shared/hooks/CellEditing';
import { useDialogCrup } from '../../../shared/hooks/DialogCrup';
import { useImExExcel } from '../../../shared/hooks/ImExExcel';
import { FormatDisplay } from '../../../shared/format';
import { useTranslation } from '../../../shared/language';

let onewayCalc = {};

// Các mã LTSan/TTSan/XXu từ API phải được giữ nguyên trong payload, còn label chỉ phục vụ hiển thị trên dropdown.
const assetTypeOptions = [
  { value: 1, label: 'Ô tô' },
  { value: 2, label: 'Mô tô xe máy' }
];

const assetConditionOptions = [
  { value: 0, label: 'Chưa qua sử dụng - Xe mới' },
  { value: 1, label: 'Đã qua sử dụng - Xe cũ' }
];

const vehicleOriginOptions = [
  { value: 0, label: 'Sản xuất, lắp ráp trong nước' },
  { value: 1, label: 'Nhập khẩu nguyên chiếc' }
];

const invoiceDetailReadOptionValue = (rowData, field) => {
  const value = rowData?.[field];
  if (value === undefined || value === null || value === '') {
    return null;
  }
  return Number(value);
};

const invoiceDetailCanEditField = (field, rowData) => {
  const vehicleOrigin = invoiceDetailReadOptionValue(rowData, InvoiceField.vehicleOrigin);
  const assetType = invoiceDetailReadOptionValue(rowData, InvoiceField.assetType);
  const assetCondition = invoiceDetailReadOptionValue(rowData, InvoiceField.assetCondition);

  switch (field) {
    case InvoiceField.safetyEnvCertNo:
      return vehicleOrigin === 1;
    case InvoiceField.factoryQCSerialNo:
      return vehicleOrigin === 0;
    case InvoiceField.payloadCapacity:
      // Trọng tải chỉ áp dụng cho ô tô đã qua sử dụng; mô tô xe máy không được mở trường này.
      return assetType === 1 && assetCondition === 1;
    case InvoiceField.manufacturingCountryCode:
    case InvoiceField.yearProduce:
    case InvoiceField.registeredAssetTypeName:
    case InvoiceField.brandName:
    case InvoiceField.vehicleModel:
    case InvoiceField.engineCapacityOrPower:
    case InvoiceField.seatingCapacity:
    case InvoiceField.vehicleLicensePlateNumber:
      return assetCondition === 1;
    default:
      return true;
  }
};

export const ChiTietHangHoa = (props) => {
  const { t } = useTranslation();

  const { invoiceGroupSetting, formulas, item, taxes, defaultTax, patchItem, getFormat, registerInvoiceId } = props;

  const { invoiceDetail, currencyCode } = item;

  const code = '05';

  const [formulaTypes, setFormulaTypes] = useState(null);

  const [columns, setColumns] = useState([]);
  const [blankItem, setBlankItem] = useState({});

  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);

  const [addButtonRowData, setAddButtonRowData] = useState(null);
  const [addButtonColumn, setAddButtonColumn] = useState(null);

  const [taxOptions, setTaxOptions] = useState([]);

  const [totalRecords, setTotalRecords] = useState(0);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);
  const [virtualItems, setVirtualItems] = useState([]);

  useEffect(() => {
    FormulaTypeService.getList({}).then(({ items }) => {
      const _formulaTypes = [];
      items.forEach(item => {
        _formulaTypes.push({ value: item.code, label: item.name });
      });
      setFormulaTypes(_formulaTypes);
    });
    UnitService.getList({
      skipCount: 0,
      maxResultCount: 1000,
    }).then(({ items }) => {
      const _units = [];
      items.forEach(item => {
        _units.push({ value: item.id, label: item.name });
      });
      setUnits(_units);
    });
  }, []);

  useEffect(() => {
    if (taxes) {
      const _taxOptions = [];
      taxes.forEach(tax => {
        _taxOptions.push({ value: tax.id, label: tax.name });
      });
      setTaxOptions(_taxOptions);
    }
  }, [taxes]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  }

  useEffect(() => {
    if (invoiceDetail?.length === 0) {
      setVirtualItems([Object.assign({ _id: `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}` }, blankItem)]);

    }
    if (invoiceDetail && invoiceDetail.length > 50) {
      setTotalRecords(invoiceDetail.length);
      setVirtualItems(invoiceDetail.slice(first, first + rows));
    } else {
      setVirtualItems(invoiceDetail);
      setTotalRecords(0);
    }
  }, [invoiceDetail, first, rows]);

  const { render: renderDialogCrupProduct, create: createProduct } = useDialogCrup({
    header: t('catalog.product'),
    dataKey: 'id',
    width: '60rem',
    fields: [
      { field: 'code', header: t('common.itemCode'), required: true, uppercase: true, InputTextProps: { maxLength: 50 }, className: 'md:col-12' },
      { field: 'name', header: t('common.itemName'), required: true, InputTextProps: { maxLength: 500 }, className: 'md:col-12' },
      { field: 'unitId', header: t('catalog.unit'), type: 'Dropdown', DropdownProps: { options: units, filter: true }, className: 'md:col-6' },
      { field: 'taxId', header: t('catalog.taxRate'), type: 'Dropdown', DropdownProps: { options: taxOptions, filter: true }, className: 'md:col-6' },
      { field: 'price', header: t('common.salePrice'), type: 'InputNumber', InputNumberProps: { min: 0, maxFractionDigits: 2 }, className: 'md:col-12' }
    ],
    createItem: (item) => {
      return ProductService.create(item);
    },
    refreshLazyData: (data) => {
      if (data) {
        onCellChange(addButtonRowData, addButtonColumn, data);
      }
    }
  });

  const { render: renderDialogCrupUnit, create: createUnit } = useDialogCrup({
    header: t('catalog.unit'),
    dataKey: 'id',
    width: '40rem',
    fields: [
      { field: 'code', header: t('common.unitCode'), required: true, uppercase: true, InputTextProps: { maxLength: 50 }, className: 'md:col-12' },
      { field: 'name', header: t('common.unitName'), InputTextProps: { maxLength: 50 }, className: 'md:col-12' }
    ],
    createItem: (item) => {
      return UnitService.create(item);
    },
    refreshLazyData: (data) => {
      if (data) {
        onCellChange(addButtonRowData, addButtonColumn, { value: data.name, label: data.name });
      }
    }
  });

  useEffect(() => {
    if (invoiceGroupSetting && invoiceGroupSetting[code] && invoiceGroupSetting[code].settings && formulaTypes && taxes.length) {
      const _columns: any = [
        { field: 'isShowOrder', header: '', dataType: 'checkbox', editable: true, style: { minWidth: '40px', maxWidth: '40px' }, rowReorder: true }
      ];
      const _blankItem: any = { isShowOrder: true, formulaType: formulaTypes[0].value };
      for (const setting of invoiceGroupSetting[code].settings) {

        const field = `${setting.fieldName}`;
        if (INVOICE_DETAIL_REMOVED_FIELDS.includes(field)) {
          continue;
        }
        const columnWidth = setting.lableWidth + 'px';

        let dataType;
        switch (setting.dataType) {
          case 'int32':
            dataType = 'number';
            _blankItem[field] = null;
            break;
          default:
            _blankItem[field] = '';
        }

        let _column;
        switch (field) {
          case InvoiceField.OrdinalNumber:
            _column = {
              field, header: setting.lable, dataType, editable: setting.readOnly,
              style: { minWidth: columnWidth, maxWidth: columnWidth }, className: 'text-center'
            };
            break;
          case InvoiceField.ProductCode:
            _column = {
              field, header: setting.lable, dataType: 'autocomplete', editable: setting.readOnly, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: {
                field: 'code',
                suggestions: products,
                completeMethod: (event) => {
                  ProductService.getList({ code: event.query }).then(({ items }) => {
                    setProducts(items);
                  });
                }, itemTemplate: (item) => {
                  return `${item.code} - ${item.name}`;
                }
              }, addButton: true, onAddButtonClick: (rowData, column) => {
                setAddButtonRowData(rowData);
                setAddButtonColumn(column);
                createProduct();
              }
            };
            break;
          case InvoiceField.unitCode:
            _column = {
              field, header: setting.lable, dataType: 'autocomplete', editable: setting.readOnly, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: {
                field: 'label',
                suggestions: units,
                completeMethod: (event) => {
                  UnitService.getList({ name: event.query }).then(({ items }) => {
                    const _units = [];
                    items.forEach(item => {
                      _units.push({ value: item.name, label: item.name });
                    });
                    setUnits(_units);
                  });
                }, itemTemplate: (item) => {
                  return item.label;
                }
              }, addButton: true, onAddButtonClick: (rowData, column) => {
                setAddButtonRowData(rowData);
                setAddButtonColumn(column);
                createUnit();
              }
            };
            break;
          case InvoiceField.ProductName:
            _column = { field, header: setting.lable, dataType, editable: setting.readOnly, style: { minWidth: columnWidth } };
            break;
          case InvoiceField.manufacturingCountryCode:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: manufacturingCountryOptions, filter: true }
            };
            break;
          case InvoiceField.vATCode:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: taxes, optionLabel: 'name' }
            };
            break;
          case InvoiceField.property:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: InvoiceService.itemProperties, }
            };
            break;
          case InvoiceField.assetType:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: assetTypeOptions }
            };
            break;
          case InvoiceField.assetCondition:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: assetConditionOptions }
            };
            break;
          case InvoiceField.vehicleOrigin:
            _column = {
              field, header: setting.lable, dataType: 'dropdown', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth },
              inputProps: { options: vehicleOriginOptions }
            };
            break;
          case InvoiceField.isHiddenReport:
            _column = {
              field, header: setting.lable, dataType: 'checkbox', editable: true, style: { minWidth: columnWidth, maxWidth: columnWidth }
            };
            _blankItem[field] = false;
            break;
          default:
            _column = { field, header: setting.lable, dataType, editable: setting.readOnly, style: { minWidth: columnWidth, maxWidth: columnWidth } };
        }
        if (dataType === 'number') {
          const fractionDigits = getFormat(setting);
          if (_column.inputProps) {
            // _column.inputProps.minFractionDigits = fractionDigits;
            // _column.inputProps.maxFractionDigits = fractionDigits;
          } else {
            _column.inputProps = {
              minFractionDigits: fractionDigits,
              maxFractionDigits: fractionDigits
            };
          }
          if (field === 'discountRate') {
            _column.inputProps.min = 0;
            _column.inputProps.max = parseFloat('99.'.padEnd(_column.inputProps.maxFractionDigits + 3, '9'));
          }

        }
        if ([
          InvoiceField.safetyEnvCertNo,
          InvoiceField.factoryQCSerialNo,
          InvoiceField.manufacturingCountryCode,
          InvoiceField.yearProduce,
          InvoiceField.registeredAssetTypeName,
          InvoiceField.ProductName,
          InvoiceField.brandName,
          InvoiceField.vehicleModel,
          InvoiceField.engineCapacityOrPower,
          InvoiceField.payloadCapacity,
          InvoiceField.seatingCapacity,
          InvoiceField.vehicleLicensePlateNumber,
        ].includes(field)) {
          // Trường vẫn hiển thị để người dùng nhận biết cấu trúc dữ liệu, nhưng chỉ cho nhập khi dòng tài sản thỏa điều kiện nghiệp vụ.
          _column.checkEditable = (rowData) => invoiceDetailCanEditField(field, rowData);
        }
        _column.maxLength = setting.maxLength;
        _columns.push(_column);
      }
      _columns.push({
        field: `formulaType`, header: t('common.calculationMethod'), dataType: 'dropdown', editable: true, style: { minWidth: '120px', maxWidth: '120px' },
        inputProps: { options: formulaTypes }
      });

      _blankItem[InvoiceField.property] = [4, 5, 6, 9].includes(props.registerInvoice.typeCompany) ? 5 : 1;
      // Dòng tài sản mới cần trạng thái khởi tạo rõ ràng để các điều kiện hiển thị trường phụ thuộc hoạt động đúng ngay từ đầu.
      _blankItem[InvoiceField.assetType] = 2;
      _blankItem[InvoiceField.assetCondition] = 0;
      _blankItem[InvoiceField.vehicleOrigin] = 0;
      _blankItem[InvoiceField.vATCode] = defaultTax;

      setColumns(_columns);
      setBlankItem(_blankItem);
    }
  }, [invoiceGroupSetting, formulaTypes, products, units, taxes, currencyCode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (blankItem && Object.keys(blankItem).length) {
      if (invoiceDetail && invoiceDetail.length === 0) {
        addItem();
      }
    }
  }, [blankItem, invoiceDetail]); // eslint-disable-line react-hooks/exhaustive-deps

  const setInvoiceDetail = (items) => {
    patchItem(code, 'invoiceDetail', items);
  }

  const onCellChange = (rowData, column, newValue) => {
    const _items = [...invoiceDetail];
    for (const _item of _items) {
      if (_item._id === rowData._id) {
        _item[column.field] = newValue;
        onewayCalc = {};

        switch (column.field) {
          case InvoiceField.ProductCode:
            if (newValue && newValue.id) {
              _item[InvoiceField.ProductName] = newValue.name;
              _item[InvoiceField.UnitCode] = newValue.unit;
              _item[InvoiceField.UnitPrice] = newValue.price;
              startCalc(_item, InvoiceField.UnitPrice);
              _item[InvoiceField.vATCode] = null;
              for (const t of taxes) {
                if (t.code === newValue.taxCode) {
                  _item[InvoiceField.vATCode] = t;
                  startCalc(_item, InvoiceField.vATCode);
                  break;
                }
              }
            }
            break;
          case InvoiceField.OrdinalNumber:
            _item['ordinalNumberManual'] = true;
            break;
          case InvoiceField.property:
            if (newValue === 4) {
              _item[InvoiceField.vATCode] = null;
              _item['formulaType'] = null;
            }

            if (!_item['ordinalNumberManual']) {
              _item.isShowOrder = newValue !== 4;
            }

            _item['frameNumber'] = null;
            _item['engineNumber'] = null;
            _item['vehicleRegistrationNumber'] = null;
            _item['senderName'] = null;
            _item['senderAddress'] = null;
            _item['senderTaxCode'] = null;
            _item['senderIdentifier'] = null;
            break;
          case InvoiceField.vehicleOrigin:
            _item[InvoiceField.safetyEnvCertNo] = null;
            _item[InvoiceField.factoryQCSerialNo] = null;
            break;
          case InvoiceField.assetType:
            // Đổi sang mô tô hoặc mã khác phải xóa tải trọng vì trường này chỉ thuộc nghiệp vụ ô tô.
            if (Number(newValue) !== 1) {
              _item[InvoiceField.payloadCapacity] = null;
            }
            break;
          case InvoiceField.assetCondition:
            if (Number(newValue) !== 1) {
              // Tên hàng hóa/thương mại không phụ thuộc tình trạng tài sản nên phải giữ lại khi đổi lựa chọn.
              [
                InvoiceField.manufacturingCountryCode,
                InvoiceField.yearProduce,
                InvoiceField.registeredAssetTypeName,
                InvoiceField.brandName,
                InvoiceField.vehicleModel,
                InvoiceField.engineCapacityOrPower,
                InvoiceField.payloadCapacity,
                InvoiceField.seatingCapacity,
                InvoiceField.vehicleLicensePlateNumber,
              ].forEach((fieldName) => {
                _item[fieldName] = null;
              });
            }
            break;
        }

        startCalc(_item, column.field);
      }
    }
    refreshItems(_items);
    setInvoiceDetail(_items);
  }

  const { render: renderDataTable, selectedItem, setSelectedItem, } = useCellEditing({
    dataKey: '_id',
    columns,
    items: virtualItems,
    onCellChange,
    onRowReorder: event => {
      refreshItems(event.value);
      setInvoiceDetail(event.value);
    },
    typeCompany: props.registerInvoice.typeCompany
  });

  const hasParams = (formula, group, field) => {
    if (formula.group === code && formula.fromFe) {
      for (let entry of JSON.parse(formula.fromFe)) {
        if (entry.type === 'parameter' && entry.groupTypeCode === group && entry.fieldName === field) {
          return true;
        }
      }
    }
    return false;
  }

  const startCalc = (_item, field) => {
    if (!onewayCalc[field]) {
      onewayCalc[field] = 0;
    }
    if (onewayCalc[field] < 50) {
      onewayCalc[field]++;

      for (const formula of formulas) {
        if ((hasParams(formula, code, field) || field === 'formulaType')
          && formula.group === code && (formula.type == null || formula.type === _item[`formulaType`])) {
          try {

            const ROUND = a => {
              for (const setting of invoiceGroupSetting[code].settings) {
                if (setting.fieldName === formula.value.replace(`${code}.`, '')) {
                  const fractionDigits = getFormat(setting);
                  a = parseFloat(parseFloat(a).toFixed(fractionDigits));
                }
              }
              return a;
            }

            let statement = '';
            for (let entry of JSON.parse(formula.fromFe)) {
              if (entry.type === 'parameter') {
                switch (entry.fieldName) {
                  case InvoiceField.vATCode:
                    let taxValue = 0;
                    if (_item[entry.fieldName] && _item[entry.fieldName].taxValue) {
                      taxValue = _item[entry.fieldName].taxValue;
                    }
                    statement += `(${taxValue})`;
                    break;
                  default:
                    statement += `(${_item[entry.fieldName] || 0})`;
                }
              } else {
                statement += entry.value;
              }
            }
            // console.log(formula, statement);
            // eslint-disable-next-line no-eval
            let result = eval(statement);
            if (typeof result === 'number') {
              if (!Number.isFinite(result) || Number.isNaN(result)) {
                result = 0;
              }
              if (`${result}`.length > 2 && `${result}`.split('.').length) {
                result = 1 * result.toPrecision(`${result}`.split('.')[0].length + 7);
              }
              _item[formula.value.replace(`${code}.`, '')] = result;
              startCalc(_item, formula.value.replace(`${code}.`, ''));
            }
          } catch (e) {
            console.log(e);
          }
        }
      }
    }
  }

  const refreshItems = (_items) => {
    let orders = 0;
    let index = 0;
    for (const i of _items) {
      if (i.isShowOrder) {
        index++;
      }
      if (!i.ordinalNumberManual) {
        if (i.isShowOrder) {
          i[InvoiceField.OrdinalNumber] = index;
        } else {
          i[InvoiceField.OrdinalNumber] = null;
        }
      }
      orders++;
      i[InvoiceField.orders] = orders;
    }
  }

  const addItem = () => {
    const _items = [...invoiceDetail];
    _items.push(Object.assign({ _id: `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}` }, blankItem));
    refreshItems(_items);
    setInvoiceDetail(_items);
  }

  const deleteItem = () => {
    if (selectedItem) {
      const _items = invoiceDetail.filter((f) => f._id !== selectedItem._id);
      refreshItems(_items);
      setInvoiceDetail(_items);
      setSelectedItem(null);
    }
  }

  const copyItem = () => {
    if (selectedItem) {
      const _items = [...invoiceDetail];
      const clone = Object.assign({}, selectedItem);
      _items.push(Object.assign(clone, { _id: `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}` }));
      refreshItems(_items);
      setInvoiceDetail(_items);
    }
  }

  const insertItem = () => {
    if (selectedItem) {
      const _items = [];
      invoiceDetail?.forEach(detail => {
        if (detail._id === selectedItem._id) {
          _items.push(Object.assign({ _id: `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}` }, blankItem));
          _items.push(detail);
        } else {
          _items.push(detail);
        }
      });
      refreshItems(_items);
      setInvoiceDetail(_items);
    }
  }

  const [display, setDisplay] = useState(false);
  const [selectableItems, setSelectableItems] = useState(null);
  const [selectedItems, setSelectedItems] = useState(null);
  const [filters] = useState({
    'productCode': { value: null, matchMode: FilterMatchMode.CONTAINS },
    'productName': { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const cellBody = (rowData, field) => {
    const fm = getFormat();
    const options = {
      minimumFractionDigits: fm,
      maximumFractionDigits: fm
    }
    return FormatDisplay.number(rowData[field], options);
  }

  const showSelectableItems = () => {
    const _selectableItems = [];
    props.relatedInvoiceDetail.forEach(r => {
      let add = true;
      invoiceDetail.forEach(d => {
        if (d.id === r.id) {
          add = false;
        }
      });
      if (add) {
        _selectableItems.push(r);
      }
    });
    setSelectableItems(_selectableItems);
    setSelectedItems(null);
    setDisplay(true);
  }

  const selectProducts = () => {
    if (selectedItems && selectedItems.length) {
      const _items = [];
      invoiceDetail.forEach(i => {
        if (i[InvoiceField.ProductName]) {
          _items.push(i);
        }
      });
      selectedItems.forEach((selectedItem, index) => {
        selectedItem._id = `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}_${index}`;
        _items.push(Object.assign({}, selectedItem));
      });
      refreshItems(_items);
      setInvoiceDetail(_items);
      setDisplay(false);
    }
  }

  const { renderImExExcel, showImportDialog } = useImExExcel({
    exportTemplate: () => {
      return InvoiceService.downloadExcelDetail(registerInvoiceId);
    },
    exportExcel: () => {
      return InvoiceService.downloadExcelDetail(registerInvoiceId);
    },
    exportExcelFileName: 'danh-sach-hang-hoa',
    importExcel: (data) => {
      return InvoiceService.uploadExcelDetail(registerInvoiceId, data);
    },
    reloadLazyData: (result) => {
      if (result && result.length) {
        const _items = [];
        result.forEach((_item, index) => {
          _item._id = `added-item-${Date.now()}_${Math.floor(Math.random() * 10000) + 1}_${index}`;
          _item.formulaType = formulaTypes[0].value;

          for (const t of taxes) {
            if (t.code === _item.vatCode) {
              _item[InvoiceField.vATCode] = t;
              break;
            }
          }

          _items.push(Object.assign({}, _item));
        });
        refreshItems(_items);
        setInvoiceDetail(_items);
      }
    }
  });

  const template: any = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
    CurrentPageReport: (options) => {
      return t('common.currentPageReport')
        .replace('{first}', options.first)
        .replace('{last}', options.last)
        .replace('{totalRecords}', options.totalRecords);
    }
  }

  useEffect(() => {
    window['preventToolbarShortcut'] = true;
    const onKeydown = event => {
      if (event && event.which) {
        switch (event.which) {
          case 118:
            copyItem();
            event.preventDefault();
            break;
          case 119:
            deleteItem();
            event.preventDefault();
            break;
          case 120:
            addItem();
            event.preventDefault();
            break;
          case 45:
            insertItem();
            event.preventDefault();
            break;
        }
      }
    }
    window.addEventListener('keydown', onKeydown);
    return () => {
      window.removeEventListener('keydown', onKeydown);
      window['preventToolbarShortcut'] = false;
    };
  }, [invoiceDetail, selectedItem]);

  if (invoiceGroupSetting && invoiceGroupSetting[code] && invoiceGroupSetting[code].settings) {
    return (
      <Fragment>
        <div className="mb-1">
          <Button icon="pi pi-plus" label={`${t('common.addLine')} (F9)`} className="p-button-outlined p-button-sm mr-2" onClick={addItem} />
          <Button icon="pi pi-trash" label={`${t('common.deleteLine')} (F8)`} className="p-button-outlined p-button-danger p-button-sm mr-2" onClick={deleteItem}
            disabled={!selectedItem} />
          <Button icon="pi pi-copy" label={`${t('common.copyLine')} (F7)`} className="p-button-outlined p-button-success p-button-sm mr-2" onClick={copyItem}
            disabled={!selectedItem} />
          <Button icon="pi pi-sign-in" label={`${t('common.insertLine')} (Ins)`} className="p-button-outlined p-button-info p-button-sm mr-2" onClick={insertItem}
            disabled={!selectedItem} />
          {([2, 3].includes(props.invoiceStatus) && props.relatedInvoiceDetail) &&
            <Fragment>
              <Button icon="pi pi-plus" label={t('common.chooseProduct')} className="p-button-outlined p-button-help p-button-sm mr-2" onClick={showSelectableItems} />
              <Dialog header={t('common.selectReplacementProduct')} visible={display} style={{ width: '80vw' }} onHide={() => setDisplay(false)}>
                <DataTable value={selectableItems} selection={selectedItems} onSelectionChange={e => setSelectedItems(e.value)}
                  showGridlines stripedRows size="small" responsiveLayout="scroll"
                  scrollable scrollHeight="flex"
                  dataKey="id" filters={filters} filterDisplay="row">
                  <Column selectionMode="multiple" style={{ minWidth: '3em', maxWidth: '3rem', justifyContent: 'center' }}></Column>
                  <Column field="ordinalNumber" header={t('common.sequenceNumber')} style={{ minWidth: '4rem', maxWidth: '4rem', justifyContent: 'center' }}></Column>
                  <Column field="productCode" header={t('common.itemCode')} filter showFilterMenu={false} style={{ minWidth: '4rem' }}></Column>
                  <Column field="productName" header={t('common.itemName')} filter showFilterMenu={false} style={{ minWidth: '4rem' }}></Column>
                  <Column field="unitCode" header={t('catalog.unit')} style={{ minWidth: '7rem', maxWidth: '7rem' }}></Column>
                  <Column field="quantity" header={t('common.quantity')} style={{ minWidth: '7rem', maxWidth: '7rem', justifyContent: 'end' }}
                    body={(rowData) => cellBody(rowData, 'quantity')}></Column>
                  <Column field="amount" header={t('common.unitPrice')} style={{ minWidth: '7rem', maxWidth: '7rem', justifyContent: 'end' }}
                    body={(rowData) => cellBody(rowData, 'amount')}></Column>
                  <Column field="amountWithoutVAT" header={t('common.amount')} style={{ minWidth: '8rem', maxWidth: '8rem', justifyContent: 'end' }}
                    body={(rowData) => cellBody(rowData, 'amountWithoutVAT')}></Column>
                  <Column field="vatName" header={t('catalog.taxRate')} style={{ minWidth: '7rem', maxWidth: '7rem', justifyContent: 'center' }}></Column>
                </DataTable>
                <div className="flex justify-content-end mt-3">
                  <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplay(false)} className="p-button-text p-button-sm" />
                  <Button label={t('common.confirm')} icon="pi pi-check" onClick={selectProducts} className="p-button-sm" />
                </div>
              </Dialog>
            </Fragment>
          }
          <Button icon="pi pi-file-excel" label={t('common.receiveExcelDetail')} className="p-button-outlined p-button-help p-button-sm mr-2" onClick={showImportDialog} />
          {totalRecords > 0 &&
            <div className="inline-flex">
              <Paginator first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} className="p-paginator-sm p-0 pr-2"
                template={template} pageLinkSize={3}></Paginator>
            </div>
          }
        </div>
        {renderDataTable()}
        {renderDialogCrupProduct()}
        {renderDialogCrupUnit()}
        {renderImExExcel()}
      </Fragment>
    );
  } else {
    return (
      <div />
    );
  }

}
